/**
 * useQueueSummary — the ONE badge/counter source for the web shell (spec 110).
 *
 * Polls the cheap counts-only `GET /queue/summary` (RBAC-scoped, 15s
 * server-side TTL cache) every 60s + on window focus. Supersedes BOTH
 * `useAlertSummary` (bell / Alerts badge) and `useHandoffQueueCounters`
 * (My Queue badge) — two polls become one.
 *
 * Enabled for every org role (incl. VIEWER — the endpoint returns their
 * property-scoped me-counts and orgCritical: 0) and for ops users actively
 * viewing an org. Disabled for logged-out / bare-ops sessions.
 */
import { useQuery, type QueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

export interface QueueSummaryData {
  visitsToday: number;
  attention: { critical: number; high: number; total: number };
  decide: {
    total: number;
    estimates: number;
    invoices: number;
    schedules: number;
    notices: number;
  };
  orgCritical: number;
  tasksOpen: number;
}

const ZERO: QueueSummaryData = {
  visitsToday: 0,
  attention: { critical: 0, high: 0, total: 0 },
  decide: { total: 0, estimates: 0, invoices: 0, schedules: 0, notices: 0 },
  orgCritical: 0,
  tasksOpen: 0,
};

function normalizeSummary(d: any): QueueSummaryData {
  return {
    visitsToday: d?.visitsToday ?? 0,
    attention: {
      critical: d?.attention?.critical ?? 0,
      high: d?.attention?.high ?? 0,
      total: d?.attention?.total ?? 0,
    },
    decide: {
      total: d?.decide?.total ?? 0,
      estimates: d?.decide?.estimates ?? 0,
      invoices: d?.decide?.invoices ?? 0,
      schedules: d?.decide?.schedules ?? 0,
      notices: d?.decide?.notices ?? 0,
    },
    orgCritical: d?.orgCritical ?? 0,
    tasksOpen: d?.tasksOpen ?? 0,
  };
}

/**
 * Client timezone as minutes EAST of UTC (W3 — the sign flip of JS's
 * `getTimezoneOffset`, which reports minutes WEST). Sent on every queue call
 * so the backend's "today" windows match the user's local day (the server
 * treats a missing param as 0/UTC).
 */
export function tzOffsetMinutes(): number {
  return -new Date().getTimezoneOffset();
}

/**
 * Best-effort FRESH summary refetch after a mutation (W2 review WR-04):
 * `?fresh=1` bypasses the server's 15s TTL cache so badges/heroes track the
 * just-changed feed instead of lagging up to 15s. Writes straight into the
 * shared ['queue-summary', tz] cache entry (same key shape as the hook);
 * failures are swallowed (the normal 60s poll heals).
 */
export async function refreshQueueSummaryFresh(queryClient: QueryClient): Promise<void> {
  try {
    const tz = tzOffsetMinutes();
    const res = await api.get('/queue/summary', { params: { fresh: 1, tzOffsetMinutes: tz } });
    const d = res.data?.data;
    if (d) queryClient.setQueryData(['queue-summary', tz], normalizeSummary(d));
  } catch {
    /* best-effort — the poll recovers */
  }
}

export function useQueueSummary() {
  const { user, isOpsUser, opsOrgAccess } = useAuth();
  const enabled =
    (!isOpsUser && user?.type === 'org') || (isOpsUser && !!opsOrgAccess);

  // Recomputed every render — a DST flip / travel changes the key → refetch.
  const tz = tzOffsetMinutes();

  const query = useQuery({
    queryKey: ['queue-summary', tz],
    queryFn: async (): Promise<QueueSummaryData> => {
      const res = await api.get('/queue/summary', { params: { tzOffsetMinutes: tz } });
      return normalizeSummary(res.data?.data ?? {});
    },
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  return { summary: query.data ?? ZERO, isLoading: query.isLoading, enabled };
}
